import useAxios, { api } from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useState } from 'react';
import { downloadFileFromBlobResponse } from '@core/utils/file';
import { toast } from "react-toastify";

const TOP_TRENDS_EXPORT_ENDPOINT: keyof paths = '/TopTrendMainExport';
const TOP_TRENDS_PPT_EXPORT_ENDPOINT: keyof paths = '/TopTrendPptExport';

export type TopTrendsExportRequest = components['schemas']['TopTrendExportFilter'];

export default function useTopTrendsExport() {
    const [isExporting, setIsExporting] = useState(false);

    const {
        doFetch: doExportXls,
        cancel: cancelXls,
    } = useAxios<string, TopTrendsExportRequest>({
        method: 'post',
        url: TOP_TRENDS_EXPORT_ENDPOINT,
        lazy: true,
    });

    const {
        doFetch: doExportPpt,
        cancel: cancelPpt,
    } = useAxios<string, TopTrendsExportRequest>({
        method: 'post',
        url: TOP_TRENDS_PPT_EXPORT_ENDPOINT,
        lazy: true,
    });

    const downloadXls = useCallback(
        async (requestData: TopTrendsExportRequest) => {

            setIsExporting(true);
            toast.info("Downloading Excel...");

            try {
                const response = await doExportXls({ data: requestData });
                const token = response?.data;

                if (token) {
                    const blobResponse = await api.get(
                        `${TOP_TRENDS_EXPORT_ENDPOINT}?key=${token}`,
                        { responseType: 'blob' }
                    );
                    await downloadFileFromBlobResponse(blobResponse);
                    toast.success("Download was successful");
                }
            } catch (err) {
                toast.error("Something went wrong");
            } finally {
                setIsExporting(false);
            }
        },
        [doExportXls]
    );

    const downloadPpt = useCallback(
        async (requestData: TopTrendsExportRequest) => {

            setIsExporting(true);
            toast.info("Downloading PowerPoint...");

            try {
                const response = await doExportPpt({ data: requestData });
                const token = response?.data;

                if (token) {
                    const blobResponse = await api.get(
                        `${TOP_TRENDS_PPT_EXPORT_ENDPOINT}?key=${token}`,
                        { responseType: 'blob' }
                    );
                    await downloadFileFromBlobResponse(blobResponse);
                    toast.success("Download was successful");
                }
            } catch (err) {
                toast.error("Something went wrong");
            } finally {
                setIsExporting(false);
            }
        },
        [doExportPpt]
    );

    const cancel = useCallback(() => {
        cancelXls();
        cancelPpt();
    }, [cancelXls, cancelPpt]);

    return {
        isExporting,
        downloadXls,
        downloadPpt,
        cancel,
    };
}
